import { createRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { rootRoute } from "./root-route";

export const casesNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/cases/new",
  beforeLoad: async () => {
    try {
      await api.me();
    } catch {
      throw redirect({
        to: "/login",
        search: { returnTo: "/cases/new", error: undefined },
      });
    }
  },
  component: NewCasePage,
});

function NewCasePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (auth.status === "loading") {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (auth.status === "anon") return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!text.trim()) {
      setError("请粘贴案件剧本以继续。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.createCase({ text: text.trim() });
      const versionId = result.version.id;
      await navigate({
        to: "/cases/$caseId/versions/$versionId/meta",
        params: {
          caseId: result.case.id,
          versionId,
        },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "script_required" || err.code === "source_required") {
          setError("请粘贴案件剧本以继续。");
        } else if (err.code === "text_too_large") {
          setError("剧本文本过大。");
        } else {
          setError(err.code);
        }
      } else {
        setError("无法创建案件。");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink render={<Link to="/" />}>案件</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>新建案件</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex flex-col gap-1">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            新建案件
          </h1>
          <p className="text-sm text-muted-foreground">
            粘贴案件剧本。下一步：在版本元信息中审阅并启动流水线。之后可随时在案件源文中编辑。若源文件是
            PDF 或图片，请先使用提取。
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>案件剧本</CardTitle>
          <CardDescription>
            仅支持剧本文本。若源文件是 PDF 或图片，请打开提取，拉取文本后再粘贴到此处。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="source-text">剧本</FieldLabel>
                <Textarea
                  id="source-text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="粘贴完整案件剧本…"
                  rows={16}
                  className="max-h-96 min-h-48 overflow-y-auto font-mono text-xs"
                  disabled={busy}
                />
                <FieldDescription>
                  必填。下一步会生成元信息草稿，并打开版本元信息关卡，供你确认后启动流水线。
                </FieldDescription>
              </Field>
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={busy || !text.trim()}>
                  {busy ? <Spinner /> : null}
                  {busy ? "创建中…" : "继续到元信息"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  render={<Link to="/" />}
                  nativeButton={false}
                >
                  取消
                </Button>
              </div>
              {error ? <FieldError>{error}</FieldError> : null}
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
